import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { CropRect, ScanMode } from '../vehicle-form-fields/scan-parsers';

type DragMode = 'move' | 'nw' | 'ne' | 'sw' | 'se';

/** Fractions of the displayed photo, so the box survives a rotate or resize. */
interface Box {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** Never let the box collapse to nothing under a clumsy drag. */
const MIN_SIZE = 0.05;

/**
 * Lets the user draw a box around the VIN (or plate) before it is read.
 *
 * With the current engine this is a fallback rather than a necessity — real
 * photos of a door-jamb sticker, an etched windscreen and a plate all read
 * exactly from the whole frame, which is why "Scan whole photo" is offered
 * alongside. The box earns its place when a photo contains more than one
 * candidate (two stickers, a plate behind a windscreen) or when a first attempt
 * picks the wrong text: everything outside it is discarded before the
 * recognizer runs.
 *
 * Follows the app's modal convention — the opener passes in its NgbModalRef
 * rather than the modal injecting NgbActiveModal.
 */
@Component({
  selector: 'app-scan-crop-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './scan-crop-modal.component.html',
  styleUrls: ['./scan-crop-modal.component.scss'],
})
export class ScanCropModalComponent implements OnInit, OnDestroy {
  @Input({ required: true }) modalRef!: NgbModalRef;
  @Input({ required: true }) file!: File;
  @Input() mode: ScanMode = 'vin';

  @ViewChild('photo') photo?: ElementRef<HTMLImageElement>;

  previewUrl: string | null = null;
  /** Starts as a wide, short band — the shape of a VIN on a sticker or a plate. */
  box: Box = { left: 0.1, top: 0.38, right: 0.9, bottom: 0.62 };

  private drag: DragMode | null = null;
  private startPointer = { x: 0, y: 0 };
  private startBox: Box = { ...this.box };

  get title(): string {
    return this.mode === 'vin' ? 'Frame the VIN' : 'Frame the plate number';
  }

  // The opener assigns `file` right after construction, so the URL is made here
  // rather than from the image's own load event — which could never fire, since
  // it is what supplies the src in the first place.
  ngOnInit(): void {
    this.previewUrl = URL.createObjectURL(this.file);
  }

  ngOnDestroy(): void {
    this.revoke();
  }

  startDrag(event: PointerEvent, mode: DragMode): void {
    event.preventDefault();
    event.stopPropagation();
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
    this.drag = mode;
    this.startPointer = { x: event.clientX, y: event.clientY };
    this.startBox = { ...this.box };
  }

  onDrag(event: PointerEvent): void {
    const image = this.photo?.nativeElement;
    if (!this.drag || !image) {
      return;
    }
    event.preventDefault();

    const dx = (event.clientX - this.startPointer.x) / image.clientWidth;
    const dy = (event.clientY - this.startPointer.y) / image.clientHeight;
    const start = this.startBox;

    if (this.drag === 'move') {
      const width = start.right - start.left;
      const height = start.bottom - start.top;
      const left = clamp(start.left + dx, 0, 1 - width);
      const top = clamp(start.top + dy, 0, 1 - height);
      this.box = { left, top, right: left + width, bottom: top + height };
      return;
    }

    const box = { ...start };
    if (this.drag === 'nw' || this.drag === 'sw') {
      box.left = clamp(start.left + dx, 0, start.right - MIN_SIZE);
    } else {
      box.right = clamp(start.right + dx, start.left + MIN_SIZE, 1);
    }
    if (this.drag === 'nw' || this.drag === 'ne') {
      box.top = clamp(start.top + dy, 0, start.bottom - MIN_SIZE);
    } else {
      box.bottom = clamp(start.bottom + dy, start.top + MIN_SIZE, 1);
    }
    this.box = box;
  }

  endDrag(): void {
    this.drag = null;
  }

  /** Convert the box to source-photo pixels — what preprocessImage expects. */
  confirm(): void {
    const image = this.photo?.nativeElement;
    if (!image) {
      return;
    }
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    const crop: CropRect = {
      x: Math.round(this.box.left * width),
      y: Math.round(this.box.top * height),
      width: Math.round((this.box.right - this.box.left) * width),
      height: Math.round((this.box.bottom - this.box.top) * height),
    };
    this.revoke();
    this.modalRef.close(crop);
  }

  /** Scan the whole photo, for when the text fills the frame already. */
  useWholePhoto(): void {
    this.revoke();
    this.modalRef.close(null);
  }

  cancel(): void {
    this.revoke();
    this.modalRef.dismiss();
  }

  private revoke(): void {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
